import React from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import { useTranslation } from 'react-i18next';

/**
 * 分組節點列表組件
 * 用於顯示和編輯分組的引導單元
 */
const GroupedNodeList = ({ nodeGroups, onChange }) => {
  const { t } = useTranslation('homepage');
  const handleNodeChange = (groupIndex, nodeIndex, field, value) => {
    const newGroups = [...nodeGroups];
    newGroups[groupIndex].nodes[nodeIndex][field] = value;
    onChange(newGroups);
  };

  const handleAddNode = (groupIndex) => {
    const newGroups = [...nodeGroups];
    const group = newGroups[groupIndex];
    
    if (!group.maxNodes || group.nodes.length < group.maxNodes) {
      group.nodes.push({ title: '', content: '', tags: [] });
      onChange(newGroups);
    }
  };

  const handleRemoveNode = (groupIndex, nodeIndex) => {
    const newGroups = [...nodeGroups];
    const group = newGroups[groupIndex];
    
    if (!group.minNodes || group.nodes.length > group.minNodes) {
      group.nodes.splice(nodeIndex, 1);
      onChange(newGroups);
    }
  };

  const handleAddTag = (groupIndex, nodeIndex, tag) => {
    if (!tag.trim()) return;
    const newGroups = [...nodeGroups];
    const node = newGroups[groupIndex].nodes[nodeIndex];
    if (!node.tags.includes(tag)) {
      node.tags.push(tag);
      onChange(newGroups);
    }
  };

  const handleRemoveTag = (groupIndex, nodeIndex, tagToRemove) => {
    const newGroups = [...nodeGroups];
    newGroups[groupIndex].nodes[nodeIndex].tags = 
      newGroups[groupIndex].nodes[nodeIndex].tags.filter(tag => tag !== tagToRemove);
    onChange(newGroups);
  };

  return (
    <Box>
      {nodeGroups.map((group, groupIndex) => (
        <Accordion key={group.groupId} defaultExpanded={groupIndex === 0}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
              <Typography variant="h6">{group.groupName}</Typography>
              <Typography variant="caption" color="text.secondary">
                {t('groupedNodeList.nodeCount', { count: group.nodes.length })}
              </Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {group.groupDescription && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {group.groupDescription}
              </Typography>
            )}

            {group.nodes.map((node, nodeIndex) => (
              <Paper key={nodeIndex} sx={{ p: 2, mb: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                  <Typography variant="subtitle2">
                    {t('groupedNodeList.nodeLabel', { index: nodeIndex + 1 })}
                  </Typography>
                  {(!group.minNodes || group.nodes.length > group.minNodes) && (
                    <IconButton 
                      size="small" 
                      color="error"
                      onClick={() => handleRemoveNode(groupIndex, nodeIndex)}
                    >
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>

                <TextField
                  fullWidth
                  label={t('groupedNodeList.nodeTitle')}
                  value={node.title}
                  onChange={(e) => handleNodeChange(groupIndex, nodeIndex, 'title', e.target.value)}
                  margin="normal"
                  size="small"
                  required
                />

                <TextField
                  fullWidth
                  label={t('groupedNodeList.nodeContent')}
                  value={node.content}
                  onChange={(e) => handleNodeChange(groupIndex, nodeIndex, 'content', e.target.value)}
                  margin="normal"
                  multiline
                  rows={3}
                  size="small"
                  required
                />

                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {t('groupedNodeList.tagsLabel')}
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                    {node.tags.map((tag, tagIndex) => (
                      <Chip
                        key={tagIndex}
                        label={tag}
                        onDelete={() => handleRemoveTag(groupIndex, nodeIndex, tag)}
                        size="small"
                      />
                    ))}
                  </Box>
                  <TextField
                    size="small"
                    placeholder={t('groupedNodeList.addTagPlaceholder')}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        handleAddTag(groupIndex, nodeIndex, e.target.value);
                        e.target.value = '';
                      }
                    }}
                    sx={{ mt: 1 }}
                  />
                </Box>
              </Paper>
            ))}

            {(!group.maxNodes || group.nodes.length < group.maxNodes) && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <IconButton
                  color="primary"
                  onClick={() => handleAddNode(groupIndex)}
                >
                  <AddIcon />
                </IconButton>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, ml: 1 }}>
                  {t('groupedNodeList.addNodeToGroup', { group: group.groupName })}
                </Typography>
              </Box>
            )}
          </AccordionDetails>
        </Accordion>
      ))}
    </Box>
  );
};

export default GroupedNodeList;
